<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <h1 class="text-[22px] sm:text-[24px] font-bold text-gray-900 mb-6">
      Registration Deeds
    </h1>

    <div v-if="!can.view" class="mt-10"></div>

    <template v-else>
      <!-- Company Dropdown -->
      <div class="mb-6 max-w-xl">
        <label class="block text-sm font-semibold text-gray-800 mb-2">Company</label>
        <select
          v-model="selectedCompany"
          class="w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm focus:outline-none focus:border-blue-500"
        >
          <option disabled value="">Select company</option>
          <option v-for="c in companies" :key="c.id" :value="c.id">
            {{ c.name }}
          </option>
        </select>
      </div>

      <!-- Table renders only when a company is selected -->
      <div v-if="selectedCompany">
        <DynamicTable
          :rows="rows"
          :columns="columns"
          :rows-per-page="50"
          :rows-per-page-options="[10, 20, 50]"
          :show-header="true"
          search-placeholder="Search"
          :show-add-button="can.create"
          add-button-label="+ New Registration Deeds"
          add-button-color="#5B9AC5"
          @add="handleAdd"
          @row-action="handleRowAction"
        >
          <template #cell:action="{ row }">
            <div class="flex items-center justify-center gap-4 text-[13px]">
              <button
                v-if="can.viewDeed"
                class="inline-flex items-center gap-1 text-gray-700 hover:text-gray-900"
                @click="openView(row)"
                title="View"
              >
                <i class="fas fa-eye"></i>
                <span>View</span>
              </button>

              <button
                v-if="can.edit"
                class="inline-flex items-center gap-1.5 text-black hover:opacity-90"
                @click="openEdit(row)"
                title="Edit"
              >
                <svg viewBox="0 0 24 24" class="w-4 h-4" aria-hidden="true">
                  <path
                    d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm18-11.5a1 1 0 0 0 0-1.41L18.66 3a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75L21 5.75Z"
                    fill="currentColor"
                  />
                </svg>
                <span>Edit</span>
              </button>

              <button
                v-if="can.edit"
                class="inline-flex items-center gap-1 text-black hover:text-red-700"
                @click="openDeleteModal(row)"
                title="Delete"
              >
                <i class="fas fa-trash-alt text-red-600"></i>
                <span>Delete</span>
              </button>

              <button
                v-if="can.file"
                class="inline-flex items-center gap-1 text-gray-700 hover:text-gray-900"
                @click="openFile(row)"
                title="File"
              >
                <i class="fas fa-file-alt"></i>
                <span>File</span>
              </button>

              <span v-if="!can.viewDeed && !can.edit && !can.file" class="text-gray-400">—</span>
            </div>
          </template>
        </DynamicTable>
      </div>

      <DeleteModal
        v-model="deleteState.open"
        :title="deleteTitle"
        :message="deleteMessage"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />
    </template>
  </div>
</template>

<script>
import api from "@/api";
import DynamicTable from "@/components/DynamicTable.vue";
import DeleteModal from "@/components/DeleteModal.vue";
import { loadMyPermissions, hasAny } from "@/utils/permissions";

export default {
  name: "RegistrationDeeds",
  components: { DynamicTable, DeleteModal },

  data() {
    return {
      can: {
        view:     false, // registration_deed.view
        viewDeed: false, // registration_deed.registration_deed.view
        create:   false, // registration_deed.registration_deed.create
        edit:     false, // registration_deed.registration_deed.edit
        file:     false, // registration_deed.registration_deed.file
        allowedCompanies: null,
      },

      selectedCompany: "",
      companies: [],

      columns: [
        { key: "no",          label: "No.",         width: "15%" },
        { key: "date",        label: "Date",        width: "30%" },
        { key: "description", label: "Description", width: "35%" },
        { key: "action",      label: "Action",      width: "20%" },
      ],
      rows: [],

      deleteState: { open: false, index: -1 },
      deleteTitle:   "Are you sure?",
      deleteMessage: "Delete this registration deed?",
    };
  },

  computed: {
    authConfig() {
      const token = localStorage.getItem("token");
      return {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      };
    },
  },

  watch: {
    selectedCompany(val) {
      if (val) {
        sessionStorage.setItem("registration_deed_selected_company_id", String(val));
        const compObj = this.companies.find((c) => String(c.id) === String(val));
        if (compObj) {
          sessionStorage.setItem("registration_deed_selected_company_name", compObj.name);
        }
        this.fetchRows();
      } else {
        sessionStorage.removeItem("registration_deed_selected_company_id");
        sessionStorage.removeItem("registration_deed_selected_company_name");
        this.rows = [];
      }
    },
  },

  beforeRouteLeave(to, from, next) {
    if (to.name !== "RegistrationDeedForm") {
      sessionStorage.removeItem("registration_deed_selected_company_id");
      sessionStorage.removeItem("registration_deed_selected_company_name");
    }
    next();
  },

  async created() {
    await this.ensurePermissions();
    if (!this.can.view) return;

    await this.fetchCompanies();

    const savedCompanyId = sessionStorage.getItem("registration_deed_selected_company_id");
    if (savedCompanyId) {
      const exists = this.companies.some((c) => String(c.id) === String(savedCompanyId));
      if (exists) {
        this.selectedCompany = savedCompanyId;
      }
    }
  },

  methods: {
    async ensurePermissions() {
      try {
        await loadMyPermissions(false);
        this.can.view     = hasAny(["registration_deed.view"]);
        this.can.viewDeed = hasAny(["registration_deed.registration_deed.view"]);
        this.can.create   = hasAny(["registration_deed.registration_deed.create"]);
        this.can.edit     = hasAny(["registration_deed.registration_deed.edit"]);
        this.can.file     = hasAny(["registration_deed.registration_deed.file"]);
      } catch (_) {
        this.can = {
          view: false,
          viewDeed: false,
          create: false,
          edit: false,
          file: false,
          allowedCompanies: null,
        };
      }
    },

    async fetchCompanies() {
      try {
        const res  = await api.get("/accounting/companies", this.authConfig);
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        let companies = data.map((c) => ({ id: c.id, name: c.name }));

        if (this.can.allowedCompanies !== null) {
          companies = companies.filter((c) => this.can.allowedCompanies.includes(c.name));
        }

        this.companies = companies;

        const savedCompanyId = sessionStorage.getItem("registration_deed_selected_company_id");
        if (savedCompanyId) {
          const compObj = this.companies.find((c) => String(c.id) === String(savedCompanyId));
          if (compObj) {
            sessionStorage.setItem("registration_deed_selected_company_name", compObj.name);
          }
        }
      } catch (e) {
        console.error("[RegistrationDeeds] fetchCompanies failed:", e);
      }
    },

    async fetchRows() {
      if (!this.can.viewDeed || !this.selectedCompany) return;
      try {
        const res = await api.get("/accounting/registration-deeds", {
          ...this.authConfig,
          params: { company_id: this.selectedCompany, per_page: 9999 },
        });
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];

        this.rows = data.map((item) => ({
          id: item.id,
          no: item.company_sequence ?? item.number ?? item.id, // استخدام company_sequence هنا
          date: item.date,
          description: item.description || item.note || "—",
        }));
      } catch (e) {
        console.error("[RegistrationDeeds] fetchRows failed:", e);
        this.rows = [];
      }
    },
    handleAdd() {
      if (!this.can.create) return;
      const company = this.companies.find((c) => String(c.id) === String(this.selectedCompany));
      this.$router.push({
        name: "RegistrationDeedForm",
        query: {
          mode: "add",
          company_id: this.selectedCompany,
          company_name: company?.name || sessionStorage.getItem("registration_deed_selected_company_name") || "",
        },
      });
    },

    handleRowAction({ action, row }) {
      if (action === "view")   return this.openView(row);
      if (action === "edit")   return this.openEdit(row);
      if (action === "delete") return this.openDeleteModal(row);
      if (action === "file")   return this.openFile(row);
    },

    openView(row) {
      if (!this.can.viewDeed) return;
      this.$router.push({ name: "RegistrationDeedForm", query: { mode: "view", id: row.id } });
    },

    openEdit(row) {
      if (!this.can.edit) return;
      this.$router.push({ name: "RegistrationDeedForm", query: { mode: "edit", id: row.id } });
    },

    openDeleteModal(row) {
      if (!this.can.edit) return;
      const idx = this.rows.indexOf(row);
      if (idx > -1) this.deleteState = { open: true, index: idx };
    },

    async confirmDelete() {
      const i = this.deleteState.index;
      if (i > -1) {
        const row = this.rows[i];
        try {
          await api.delete(`/accounting/registration-deeds/${row.id}`, this.authConfig);
          this.rows.splice(i, 1);
        } catch (e) {
          console.error("[RegistrationDeeds] confirmDelete failed:", e);
        }
      }
      this.deleteState = { open: false, index: -1 };
    },

    cancelDelete() {
      this.deleteState = { open: false, index: -1 };
    },

    openFile(row) {
      if (!this.can.file) return;
      console.log("File →", row);
    },
  },
};
</script>
